/**
 * "Allocate" for a day: the batch call and the staged dialog that narrates
 * what the allocator considers while the request is in flight. The numbers
 * the dialog lands on are the server's own.
 */
import { useCallback, useMemo, useState } from "react";

import rosterAssignmentsApi, { BatchAllocationResult } from "../../../../services/roster-assignments-api";

export interface UseAllocationArgs {
	locationId: number;
	/** Reload the week once the allocator has written its assignments. */
	onAllocated: () => void | Promise<void>;
}

export function useAllocation({ locationId, onAllocated }: UseAllocationArgs) {
	const [allocating, setAllocating] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [result, setResult] = useState<BatchAllocationResult | null>(null);
	const [error, setError] = useState<string | null>(null);
	/** The days the last run covered: one day, or the week from its Monday. */
	const [span, setSpan] = useState<{ start: Date; days: number } | null>(null);

	const allocateDay = useCallback(
		async (date: Date) => {
			setAllocating(true);
			setResult(null);
			setError(null);
			setSpan({ start: date, days: 1 });
			setDialogOpen(true);

			try {
				const outcome: BatchAllocationResult = await rosterAssignmentsApi.allocateForDate(date.toISOString(), locationId);
				if (outcome.success) {
					setResult(outcome);
					void onAllocated();
				} else {
					setError("No workers could be assigned. Check labor standards and worker availability.");
				}
			} catch (err) {
				console.error("Batch allocation failed:", err);
				setError(err instanceof Error ? err.message : "Allocation failed");
			} finally {
				setAllocating(false);
			}
		},
		[locationId, onAllocated]
	);

	/** Allocate Monday to Sunday of the week, one day at a time; the dialog shows the week's totals. */
	const allocateWeek = useCallback(
		async (weekStart: Date) => {
			setAllocating(true);
			setResult(null);
			setError(null);
			setSpan({ start: weekStart, days: 7 });
			setDialogOpen(true);
			try {
				let totals: BatchAllocationResult | null = null;
				for (let i = 0; i < 7; i += 1) {
					const day = new Date(weekStart);
					day.setDate(day.getDate() + i);
					const outcome: BatchAllocationResult = await rosterAssignmentsApi.allocateForDate(day.toISOString(), locationId);
					if (!totals) {
						totals = { ...outcome, results: [...(outcome.results ?? [])], summary: { ...outcome.summary } };
					} else {
						totals.success = totals.success || outcome.success;
						totals.results = [...totals.results, ...(outcome.results ?? [])];
						const sum = totals.summary as unknown as Record<string, unknown>;
						const add = outcome.summary as unknown as Record<string, unknown>;
						Object.keys(add).forEach((key) => {
							if (typeof add[key] === "number") sum[key] = ((sum[key] as number) ?? 0) + (add[key] as number);
							else if (Array.isArray(add[key])) sum[key] = [...((sum[key] as unknown[]) ?? []), ...(add[key] as unknown[])];
						});
					}
				}
				if (totals?.success) {
					setResult(totals);
					void onAllocated();
				} else {
					setError("No workers could be assigned this week. Check labor standards, shifts and worker availability.");
				}
			} catch (err) {
				console.error("Week allocation failed:", err);
				setError(err instanceof Error ? err.message : "Allocation failed");
			} finally {
				setAllocating(false);
			}
		},
		[locationId, onAllocated]
	);

	const closeDialog = useCallback(() => setDialogOpen(false), []);

	return useMemo(
		() => ({ allocating, dialogOpen, result, error, span, allocateDay, allocateWeek, closeDialog }),
		[allocating, dialogOpen, result, error, span, allocateDay, allocateWeek, closeDialog]
	);
}

export type Allocation = ReturnType<typeof useAllocation>;
